import * as React from 'react'
import { FooterBackgroundWrapper } from '@components/FooterBackgroundWrapper'
import { PriceExplanationDescription } from '@components/PriceInfo/PriceExplanationDescription'
import { getLayoutConfig, layoutName } from '../config'
import {
  getContactLink,
  getCopyright,
  getHomepageLink,
  getImprintLink,
  getPersonalDataProcessingLink,
  getTermsAndConditionsLink,
} from '../helpers'
import { useBookingFormStore } from '../providers/BookingFormStoreProvider'
import Container from './Container'
import styles from './Footer.module.scss'
import Typography from './Typography'

const ContactPhoneIcon = () => (
  <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24">
    <path
      d="M21 16.92V20a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 1.12 4.18 2 2 0 0 1 3.11 2h3.09a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.61a2 2 0 0 1-.45 2.11L7.12 9.76a16 16 0 0 0 7.12 7.12l1.32-1.31a2 2 0 0 1 2.11-.45c.83.33 1.71.56 2.61.68A2 2 0 0 1 21 16.92Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
)

const ContactMailIcon = () => (
  <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24">
    <rect
      height="16"
      rx="2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      width="20"
      x="2"
      y="4"
    />
    <path
      d="m3 6 9 7 9-7"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
)

const Footer: React.FunctionComponent<React.PropsWithChildren> = () => {
  const language = useBookingFormStore((state) => state.language)
  const languageInitialized = useBookingFormStore((state) => state.languageInitialized)
  const step = useBookingFormStore((state) => state.stepper.step)
  const layoutConfig = getLayoutConfig()

  const showFooterBackground = step === 1
  const showFooterTopOffset = step > 1 && step !== 5
  const isFrantiskovyLazneLayout = layoutConfig.id === layoutName.frantiskovylazne
  const copy = layoutConfig.footer.copy[language] || layoutConfig.footer.copy.de
  const directContact = layoutConfig.footer.directContact
  const imprintLink = getImprintLink(language)
  const showDirectContact = !isFrantiskovyLazneLayout && !!directContact
  const phoneHref = directContact ? `tel:${directContact.phone.replace(/[^\d+]/g, '')}` : null
  const emailHref = directContact ? `mailto:${directContact.email}` : null

  return (
    <>
      {languageInitialized && (
        <FooterBackgroundWrapper
          footerWithBackground={showFooterBackground}
          withTopOffset={showFooterTopOffset}
        >
          <Container className={styles.root} component="footer">
            {showFooterBackground && (
              <div className={styles.rootPriceExplanation}>
                <PriceExplanationDescription />
              </div>
            )}
            <div className={styles.rootTopRow}>
              <section className={styles.rootSection}>
                <Typography className={styles.rootSectionTitle} component="h2" variant="small">
                  {copy.sectionTitle}
                </Typography>
                {!showDirectContact ? (
                  <Typography className={styles.rootContactLead} component="p" variant="small">
                    {copy.contactDescription}
                  </Typography>
                ) : null}
                {showDirectContact && directContact && phoneHref && emailHref ? (
                  <div className={styles.rootContactDetails}>
                    <a className={styles.rootContactDetail} href={phoneHref}>
                      <span className={styles.rootContactIcon}>
                        <ContactPhoneIcon />
                      </span>
                      <span>{directContact.phone}</span>
                    </a>
                    <a className={styles.rootContactDetail} href={emailHref}>
                      <span className={styles.rootContactIcon}>
                        <ContactMailIcon />
                      </span>
                      <span>{directContact.email}</span>
                    </a>
                  </div>
                ) : (
                  <nav className={styles.rootLinkList}>
                    <a
                      className={styles.rootPrimaryLink}
                      href={getContactLink(language)}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {copy.contactLink}
                    </a>
                  </nav>
                )}
              </section>
              <section className={styles.rootSection}>
                <Typography className={styles.rootMetaTitle} component="h2" variant="small">
                  {copy.infoTitle}
                </Typography>
                <nav className={styles.rootLinkList}>
                  <a href={getHomepageLink(language)} rel="noopener noreferrer" target="_blank">
                    {copy.homepageLink}
                  </a>
                </nav>
              </section>
              <section className={styles.rootSection}>
                <Typography className={styles.rootMetaTitle} component="h2" variant="small">
                  {copy.legalTitle}
                </Typography>
                <nav className={styles.rootLinkList}>
                  <a
                    href={getTermsAndConditionsLink(language)}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {copy.termsLink}
                  </a>
                  <a
                    href={getPersonalDataProcessingLink(language)}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {copy.privacyLink}
                  </a>
                  {imprintLink && copy.imprintLink ? (
                    <a href={imprintLink} rel="noopener noreferrer" target="_blank">
                      {copy.imprintLink}
                    </a>
                  ) : null}
                </nav>
              </section>
            </div>
            <div className={styles.rootBottomRow}>
              <div className={styles.rootCopyright}>
                <Typography component="div" variant="caption">
                  {new Date().getFullYear()} &copy; {getCopyright()}
                </Typography>
              </div>
            </div>
          </Container>
        </FooterBackgroundWrapper>
      )}
    </>
  )
}

export default Footer
